// University of Georgia: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - UGA's Common Data Set 2025-2026 (Office of Institutional Research PDF):
//    C1 applicants/admits/enrollees for Fall 2025 with the residency table,
//    C8 test policy for Fall 2027, C21/C22 plans.
//  - UGA Admissions pages: Deadlines, First-Year Applicants, First-Year
//    Criteria. The deadlines table carries no year label; it is the page as
//    posted for the application that opened August 1, 2026 (Fall 2027 entry).
//  - UGA Student Financial Aid, Costs: the 2026-2027 (Fall 2026 and Spring
//    2027) cost of attendance for Georgia residents and non-residents living
//    on campus. The CDS G1 lists slightly different 2026-2027 tuition
//    ($10,034 in-state, $30,878 out-of-state); the financial aid office's
//    published budget is used here because it is itemized for both columns.
//  - College Scorecard API, school id 139959 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UGA Common Data Set 2025-2026',
        url: 'https://oir.uga.edu/wp-content/uploads/UGA_CDS_2025-2026.pdf',
        read: '2026-09-30',
    },
    deadlines: {
        label: 'UGA Admissions, Deadlines',
        url: 'https://admissions.uga.edu/apply/deadlines/',
        read: '2026-09-30',
    },
    firstYear: {
        label: 'UGA Admissions, First-Year Applicants',
        url: 'https://admissions.uga.edu/apply/first-year-applicants/',
        read: '2026-09-30',
    },
    criteria: {
        label: 'UGA Admissions, First-Year Criteria',
        url: 'https://admissions.uga.edu/apply/first-year-applicants/first-year-criteria/',
        read: '2026-09-30',
    },
    costs: {
        label: 'UGA Student Financial Aid, Costs',
        url: 'https://osfa.uga.edu/costs/',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?139959-University-of-Georgia',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-georgia',
    kbId: 'university_of_georgia',
    name: 'University of Georgia',
    shortName: 'UGA',
    location: 'Athens, GA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (UGA brand guidelines); decoration only.
    branding: {
        primary: '#BA0C2F',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_georgia_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 47464,
        admitted: 16257,
        enrolled: 6195,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 21110, admitted: 8893 },
            { group: 'Out-of-state', applied: 25261, admitted: 7044 },
            { group: 'International', applied: 1092, admitted: 319 },
        ],
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UGA\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major. You apply to UGA, not to a major.',
        source: 'cds',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Action', deadline: 'October 15', decision: 'Mid to late November for Georgia residents, mid December for others', binding: false },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'Mid March', binding: false },
        ],
        note: 'Test scores, transcripts and the school report are due by October 29 for Early Action and January 15 for Regular Decision. The commitment deposit is due May 1.',
        source: 'deadlines',
        binding: 'Neither plan is binding, and both get the same review. In Early Action you can be admitted, deferred, waitlisted or denied. UGA says many students are admitted early, but most have their decision deferred to the Regular Decision timeline.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'UGA requires official SAT or ACT scores from every first-year applicant. There is no test-optional pathway. Scores count from exams taken and requested by the materials deadline.',
        source: 'criteria',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [10134, 31804] },
            { item: 'Fees', amounts: [1494, 1494] },
            { item: 'Housing (on campus)', amounts: [7722, 7722] },
            { item: 'Food', amounts: [4718, 4718] },
            { item: 'Books, course materials, supplies and equipment', amounts: [978, 978] },
            { item: 'Transportation', amounts: [1584, 2150] },
            { item: 'Miscellaneous living expenses', amounts: [3290, 3290] },
        ],
        totals: [29920, 52156],
        note: 'For students living on campus in Athens. Living in East Campus Village adds $1,376 to the housing cost for the year.',
        source: 'costs',
        netPrice: {
            average: 13636,
            byIncome: [
                { income: '$0 to $30,000', amount: 8995 },
                { income: '$30,001 to $48,000', amount: 8715 },
                { income: '$48,001 to $75,000', amount: 11169 },
                { income: '$75,001 to $110,000', amount: 15969 },
                { income: 'Over $110,000', amount: 18564 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UGA\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
